import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CertificationProviderStatusEnum } from 'app/library/certification-provider/enums/certification-provider-status.enum';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { FileCommand } from 'app/projects/core/src/lib/models/file.command';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICertificationQuery } from '../interfaces/certification.query.interface';
import { Certification } from '../models/certification';
import { CertificationCommand } from '../models/certification.command.model';
import { InternalCertificationCommand } from '../models/internal-certification.command.model';

export interface ICertificationsAndTotalCount {
    data: ICertificationQuery[];
    pagination: {
        number_of_pages: number;
        number_of_results: number;
    };
}

@Injectable()
export class CertificationApiService extends EntityService<ICertificationQuery, CertificationCommand> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {
        super(http, environment.apiUrl, 'certifications');
    }

    getCertifications(pageSize = 10, pageIndex = 1, search = '', provider: string[] = [], statuses: number[] = []): Observable<ICertificationsAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (provider) {
            const providerNumbers = provider.map(Number).filter((num) => !isNaN(num));

            if (providerNumbers.length > 0) {
                params = params.set('filter[provider]', providerNumbers.join(','));
            }
        }

        if (statuses) {
            const statusesNumbers = statuses.map(Number).filter((num) => !isNaN(num));

            if (statusesNumbers.length > 0) {
                params = params.set('filter[statuses]', statusesNumbers.join(','));
            }
        }

        return this.http.get<ICertificationsAndTotalCount>(`${this.environment.apiUrl}/${this.entities}`, { params });
    }

    getAll(): Observable<Certification[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => new Certification(query))));
    }

    create(name: string, providerId: string, hasExpirationDate: boolean, targetCount?: number): Observable<Certification> {
        const data = new CertificationCommand(name, providerId, null, hasExpirationDate, targetCount);

        return this._create(data).pipe(map((query) => new Certification(query)));
    }

    move(certificationId: string, providerId: string): Observable<Certification> {
        return this.http
            .put<ICertificationQuery>(`${this.environment.apiUrl}/${this.entities}/${certificationId}/move`, { provider_id: parseInt(providerId) })
            .pipe(map((query) => new Certification(query)));
    }

    createInternal(name: string, providerId: string, validityPeriod: number, file: File, hasExpirationDate: boolean, targetCount?: number): Observable<Certification> {
        const data = new InternalCertificationCommand(name, providerId, validityPeriod, file, hasExpirationDate, targetCount).formData;

        return this.http.post<ICertificationQuery>(`${this.environment.apiUrl}/${this.entities}/internal`, data).pipe(map((query) => new Certification(query)));
    }

    read(id: string): Observable<Certification> {
        return this._read(id).pipe(map((query) => new Certification(query)));
    }

    update(id: string, name: string, validityPeriod?: number, hasExpirationDate?: boolean, targetCount?: number): Observable<Certification> {
        const data = new CertificationCommand(name, null, validityPeriod, hasExpirationDate, targetCount);

        return this._update(id, data).pipe(map((query) => new Certification(query)));
    }

    async updateTemplate(id: string, file: File): Promise<Certification> {
        const data = new FileCommand('file', new File([file], '', { type: file.type }), file.name).formData;

        return this.http
            .post<ICertificationQuery>(`${this.environment.apiUrl}/${this.entities}/${id}/update-template`, data)
            .pipe(map((query) => new Certification(query)))
            .toPromise();
    }

    updateStatus(id: string, status: CertificationProviderStatusEnum): Observable<Certification> {
        return this.http.patch<ICertificationQuery>(`${this.environment.apiUrl}/${this.entities}/${id}/status`, { status }).pipe(map((query) => new Certification(query)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }
}
